import type { LevelDef } from './types';

/**
 * Day 7 —Wednesday, snow. The first snow of the year: everything is quiet
 * and bright, and every step leaves a mark. Her footprints show where she has
 * been. Someone else's are already there — going the way she is going.
 */
export const night6: LevelDef = {
  id: 'night6',
  night: 7,
  title: 'First Snow',
  subtitle: 'Wednesday · 23:38 · snow',
  look: 'snow',
  card: ['The first snow of the year.', 'The whole town is muffled and white.', 'You haven\'t taken the train since Monday.'],
  outfit: 'winter',
  clockStart: 23 * 60 + 38,
  map: [
    '#########################', // 0
    '##############H##########', // 1
    '##############.##########', // 2
    '##############.##########', // 3
    '####.....B.....##########', // 4
    '####.############.#######', // 5
    '####L############.A######', // 6
    '####.############L#######', // 7
    '####.############.#######', // 8
    '####............V....####', // 9
    '##########p#########.####', // 10
    '##########p#########.####', // 11
    '##########p#########.####', // 12
    '##########p#########L####', // 13
    '##########p#########.####', // 14
    '##########p#########.####', // 15
    '##########p#########.####', // 16
    '##########G#########.####', // 17
    '######...........T...####', // 18
    '######.#######.#####.####', // 19
    '######.#######.#####.####', // 20
    '######.#######.#####.####', // 21
    '######F#######.#####.####', // 22
    '######.#######V#####.####', // 23
    '######.#######.#####L####', // 24
    '######.#######.#####.####', // 25
    '######.#######.#####.####', // 26
    '######....L....#####.####', // 27
    '##############.#####.####', // 28
    '##############.#####.####', // 29
    '##############V..L...####', // 30
    '##############.#####.####', // 31
    '##############.#####.####', // 32
    '##############.#####.####', // 33
    '####.....L.....#####.####', // 34
    '####.###.###########L####', // 35
    '####.#gggggg########.####', // 36
    '####.#ggggtg########.####', // 37
    '####.#ggtggg########.####', // 38
    '####L#gggggg########.####', // 39
    '####.#gggggg########F####', // 40
    '####........########.####', // 41
    '####.###############.####', // 42
    '####.###############.####', // 43
    '####....V........L...####', // 44
    '############.############', // 45
    '############.############', // 46
    '############L############', // 47
    '############.############', // 48
    '############S############', // 49
    '############.############', // 50
    '############P############', // 51
  ],
  zones: [
    { r0: 35, zone: 1 },
    { r0: 18, r1: 34, zone: 2 },
    { r1: 17, zone: 3 },
  ],
  battery: 22,
  pace: { tension: 0.6, patience: 760 },
  weather: 'snow',
  theme: {
    sky: '#141a24',
    fog: { color: '#2a3140', density: 0.03 },
    hemi: { sky: '#8796b4', ground: '#3a3f48', intensity: 1.1 },
    ambient: 0.7,
    moon: 0.3,
    overcast: true,
    snow: true,
    asphalt: '#3a3c40',
    roofs: ['#d9dee6', '#cfd5de', '#e3e7ee', '#c8ced8'],
  },
  stranger: { walkMax: 2.95, chaseSpeed: 3.1, gapMax: 32, spawnDelay: 6 },
  // His older trail: down from her street, along the upper road, waiting at the shrine gate.
  footprints: [
    [
      [14.5, 3.5],
      [14.5, 4.5],
      [17.5, 4.5],
      [17.5, 9.5],
      [20.5, 9.5],
      [20.5, 18.5],
      [10.5, 18.5],
      [10.5, 17.6],
    ],
    [
      [20.5, 30.5],
      [20.5, 18.6],
    ],
  ],
  lines: {
    intro: 'Snow. It\'s so quiet I can hear my own breathing.',
    firstSight: 'Footsteps in the snow behind me. And him.',
    prints: 'Footprints. Big ones. Already here before me…',
    printsGate: 'They stop at the shrine gate. He stood here. For a long time.',
    printsHome: 'They go up my street. He\'s been to my street.',
    lawn: 'The park. Every step I take, he can follow.',
    deadend: 'Dead end. My footprints will show him I came here.',
    wrongBuilding: 'Not mine. Keep going.',
    homeStretch: 'Nearly there. Don\'t slip. Don\'t slip.',
  },
  mom: {
    first: { node: 'start', at: 6 },
    script: {
      start: {
        text: "It's snowing! Did you take the bus like I said?",
        options: [
          { text: 'Yes', end: 'Good girl. Careful on the ice.' },
          { text: "I'm walking", next: 'walking' },
        ],
      },
      walking: {
        text: 'In the snow? Alone?',
        options: [
          { text: "It's fine, it's pretty", end: "Ok... share your location with me." },
          { text: "I didn't want to see him on the bus", next: 'him' },
        ],
      },
      him: {
        text: 'Oh honey. I\'ll come and meet you. Where are you?',
        options: [
          { text: 'Near the park', end: "I'm putting my boots on." },
          { text: "Don't, it's icy", end: 'Then text me every five minutes.' },
        ],
      },
      prints: {
        text: 'Are you nearly home? Dad shovelled the steps.',
        options: [
          { text: 'Nearly', end: 'Ok. Kettle is on.' },
          { text: "Someone's footprints go to our street", next: 'footprints' },
        ],
      },
      footprints: {
        text: "Lots of people walk on our street. Don't scare yourself.",
        options: [
          { text: 'You\'re right', end: 'Come home and warm up.' },
          { text: "They're his", end: "...I'll turn the porch light on." },
        ],
      },
    },
  },
  onEnterCell(ctx, cell) {
    const key = `${cell.c},${cell.r}`;
    if (cell.r === 30 && cell.c >= 18) ctx.sayOnce('prints');
    if (cell.r === 36 || cell.r === 37) {
      if (cell.c >= 6 && cell.c <= 11) ctx.sayOnce('lawn');
    }
    if (key === '10,18' || key === '10,17') {
      if (!ctx.said('printsGate')) {
        ctx.spike(0.06);
        ctx.sayOnce('printsGate');
      }
    }
    if (key === '17,8' || key === '17,9') {
      if (!ctx.said('printsHome')) {
        ctx.spike(0.07);
        ctx.sayOnce('printsHome');
      }
    }
    if (key === '5,41' && !ctx.said('deadend')) {
      ctx.spike(0.05);
      ctx.sayOnce('deadend');
    }
    if (cell.r === 4) ctx.sayOnce('homeStretch');
  },
  onZoneChange(ctx, zone) {
    if (zone === 2) ctx.momLater('prints', 14);
    if (zone === 3) ctx.notify('yui6', 'Yui', 'r u ok?? u havent replied all day', 'normal', 4);
  },
};
